import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  makeStyles,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import { Controller, useForm, useWatch } from "react-hook-form";
import { nameof } from "ts-simple-nameof";
import { useDispatch, useSelector } from "react-redux";
import {
  ChangeApplicationStatusModel,
  ExpertsApplyModelDialogProps,
} from "../types";
import { ApplicationStatus } from "../../domain/ExpertsApplyFormModel";
import { Error } from "../common/Error";
import { updateApplicationStatus } from "../../redux/actions";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { fieldLabels } from "../Sections/fieldLabels";
import { useState } from "react";

import { authSelector } from "../../redux/selectors";
import { UserRole } from "../../domain/userRole";


const defaultValues: Partial<ChangeApplicationStatusModel> = {
  status: undefined,
};

const useStyles = makeStyles(() => ({
  actions: {
    padding: 15,
  },
}));

export const ChangeApplicationStatusDialog: React.FC<
  ExpertsApplyModelDialogProps
> = ({ item, onClose }) => {
  const { handleSubmit, control, errors } =
    useForm<ChangeApplicationStatusModel>({
      defaultValues,
    });

  const classes = useStyles();
  const dispatch = useDispatch();
  const { status } = useWatch<ChangeApplicationStatusModel>({
    control,
    name: [nameof<ChangeApplicationStatusModel>((x) => x.status)],
  });
  const [check, setCheck] = useState<boolean>(false);
  const [checkError, setCheckError] = useState<boolean>(false);

  const { roles } = useSelector(authSelector);
  const evaluator =
    roles.includes(UserRole.OfqualExpertsEvaluator) ||
    roles.includes(UserRole.ITAdmin);
  const administrator =
    (roles.includes(UserRole.OfqualExpertsAdmin) ||
      roles.includes(UserRole.ITAdmin));
  
  const onSubmit = (
    changeApplicationStatusModel: ChangeApplicationStatusModel
  ) => {
    if (
      parseInt(changeApplicationStatusModel.status!.toString()) ===
        ApplicationStatus["Evaluation unsuccessful"] ||
      parseInt(changeApplicationStatusModel.status!.toString()) ===
        ApplicationStatus["Evaluation partially approved"]
    ) {
      if (!check) {
        setCheckError(true);
        return;
      } else if (errors.feedbackToCitizen) {
        return;
      }
    }
    dispatch(updateApplicationStatus(item.id, changeApplicationStatusModel));
    onClose();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="change-application-status-dialog"
    >
      <DialogTitle data-test="activity-title">Change Status</DialogTitle>
      <Divider component="div" />
      <DialogContent style={{ minHeight: "250px" }}>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={nameof<ChangeApplicationStatusModel>((x) => x.status)}
                rules={{
                  required: true,
                }}
                render={({ onChange, value }) => (
                  <>
                    <label>Set status of current SMS application</label>
                    <RadioGroup
                      name="radio-buttons-group"
                      value={value}
                      onChange={onChange}
                    >
                      {administrator && (
                        <FormControlLabel
                          control={
                            <Radio
                              value={ApplicationStatus["Ready for evaluation"]}
                              checked={
                                parseInt(value) ===
                                ApplicationStatus["Ready for evaluation"]
                              }
                              data-test="ready-for-evaluation-radio"
                            />
                          }
                          label="Ready for evaluation"
                        />
                      )}
                      {evaluator && (
                        <FormControlLabel
                          control={
                            <Radio
                              value={
                                ApplicationStatus["Evaluation partially approved"]
                              }
                              checked={
                                parseInt(value) ===
                                ApplicationStatus["Evaluation partially approved"]
                              }
                              data-test="ready-for-evaluation-radio"
                            />
                          }
                          label="Partially Approve/Rejected"
                        />
                      )}
                      {evaluator && (
                        <FormControlLabel
                          control={
                            <Radio
                              value={ApplicationStatus["Evaluation approved"]}
                              checked={
                                parseInt(value) === ApplicationStatus["Evaluation approved"]
                              }
                              data-test="approve-application-radio"
                            />
                          }
                          label="Fully Approved"
                        />
                      )}
                      {evaluator && (
                        <FormControlLabel
                          control={
                            <Radio
                              value={ApplicationStatus["Evaluation unsuccessful"]}
                              checked={
                                parseInt(value) === ApplicationStatus["Evaluation unsuccessful"]
                              }
                              data-test="reject-application-radio"
                            />
                          }
                          label="Fully Rejected"
                        />
                      )}
                      {administrator &&
                        (item.applicationStatus ===
                          ApplicationStatus["Evaluation approved"] ||
                          item.applicationStatus ===
                            ApplicationStatus[
                              "Evaluation partially approved"
                            ]) && (
                          <FormControlLabel
                            control={
                              <Radio
                                value={ApplicationStatus["Admin approved"]}
                                checked={
                                  parseInt(value) ===
                                  ApplicationStatus["Admin approved"]
                                }
                                data-test="admin-approved-radio"
                              />
                            }
                            label="Admin approved"
                          />
                        )}
                      {administrator && (item.applicationStatus === ApplicationStatus["Evaluation approved"] || item.applicationStatus === ApplicationStatus["Evaluation partially approved"])
                        && (
                          <FormControlLabel
                            control={
                              <Radio
                                value={ApplicationStatus["Admin unsuccessful"]}
                                checked={
                                  parseInt(value) ===
                                  ApplicationStatus["Admin unsuccessful"]
                                }
                                data-test="admin-rejected-radio"
                              />
                            }
                            label="Admin unsuccessful"
                          />
                        )}
                    </RadioGroup>
                    {status &&
                      (parseInt(status.toString()) ===
                        ApplicationStatus["Admin unsuccessful"] ||
                        parseInt(status.toString()) ===
                          ApplicationStatus["Evaluation partially approved"] ||
                        parseInt(status.toString()) ===
                          ApplicationStatus["Evaluation unsuccessful"]) && (
                        <Controller
                          control={control}
                          rules={{ required: true }}
                          name="feedbackToCitizen"
                          as={
                            <TextField
                              label={fieldLabels.feedbackToCitizen}
                              data-test="status-feedback-reason-field"
                              aria-label="FeedbackToCitizen"
                              multiline
                              rows={5}
                            />
                          }
                        />
                      )}

                    {status &&
                      (parseInt(status.toString()) ===
                        ApplicationStatus["Evaluation partially approved"] ||
                        parseInt(status.toString()) ===
                          ApplicationStatus["Evaluation unsuccessful"] ||
                        parseInt(status.toString()) ===
                          ApplicationStatus["Admin unsuccessful"]) && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              data-test="status-feedback-response-checkbox"
                              onChange={(e) => setCheck(e.target.checked)}
                            />
                          }
                          label="I understand that this communication is directed at, and visible to, the citizen *"
                          labelPlacement="start"
                          style={{
                            color: checkError ? "red" : undefined,
                          }}
                        />
                      )}
                  </>
                )}
              />
              {errors.status?.type === "required" && (
                <Grid item xs={12}>
                  <Error data-test="set-application-status-validation">
                    This field is required
                  </Error>
                </Grid>
              )}
              {errors.feedbackToCitizen?.type === "required" && (
                <Grid item xs={12}>
                  <Error data-test="set-application-rejected-validation">
                    The Feedback To Citizen field is required
                  </Error>
                </Grid>
              )}
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="return-change-status-button"
        >
          Return
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          data-test="continue-change-status-button"
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};
